import React from 'react'
import { connect } from 'react-redux'
import { first, isNil, uniq } from 'lodash-es'
import { compose } from '@reduxjs/toolkit'
import { NegotiationDetail, NegotiationDetailWithMeta, NegotiationActions } from 'api'
import { GlobalState } from 'store'
import {
  createInitiatedPackageNegotiationsSummarySelector,
  createSelectedNegotiationDetailSummaryWithMeta,
  createSelectedNegotiationDetailWithMeta,
  initiatedNegotiationsCountByRfqIdGetterSelector,
  initiatedSummaryNegotiationsCountByCollectionIdGetterSelector
} from 'selectors/negotiations'
import { withModal, ModalConsumerProps, NotificationType } from 'view/shared/modal'
import { Translation } from 'locale'
import { canCancel } from 'view/routes/requests/shared/utils'
import { TypedCellRendererParams } from './gridUtils'
import { notNilFilter } from 'utils'
import { IRowNode } from 'ag-grid-community'
import { isNotEmpty, isNotNil } from 'utils/predicate'

interface ActionsButtonProps extends TypedCellRendererParams<NegotiationDetail> {
  onCancelBulk: (negotiationIds: string[], canCancelAndStage: boolean) => void
  onCancelPackage: () => void
  canCancelPackage: boolean
}

interface CounterpartyNegotiationOwnProps extends TypedCellRendererParams<NegotiationDetail> {
  node: IRowNode<NegotiationDetail>
}

interface CounterpartyNegotiationStateProps {
  onCancelBulk: typeof NegotiationActions.cancelBulk
  packageNegotiationSummaries?: NegotiationDetail[]
  selectedNegotiationDetail?: NegotiationDetailWithMeta
  selectedNegotiationDetailSummary?: NegotiationDetailWithMeta
  getNegotiationCountWithRfqId: (rfqId: string) => number
  getNegotiationCountWithCollectionId: (collectionId: string) => number
}

export interface CounterpartyNegotiationProps
  extends CounterpartyNegotiationOwnProps,
    CounterpartyNegotiationStateProps,
    ModalConsumerProps {}

export function withCounterpartyNegotiation(Component: React.ComponentType<ActionsButtonProps>) {
  class CounterpartyNegotiation extends React.Component<CounterpartyNegotiationProps> {
    render() {
      const {
        props: { onCancelBulk, packageNegotiationSummaries, ...rest }
      } = this

      const canCancelPackage =
        Array.isArray(packageNegotiationSummaries) && packageNegotiationSummaries.every(negotiationDetail => canCancel(negotiationDetail))

      return (
        <Component
          {...rest}
          onCancelPackage={this.handleCancelPackage}
          onCancelBulk={this.handleCancel}
          canCancelPackage={canCancelPackage}
        />
      )
    }

    private handleCancel = async (negotiationIds: string[], canCancelAndStage: boolean) => {
      const {
        props: { showDialog, onCancelBulk }
      } = this

      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_CANCEL_ROW_RFQ,
        message: Translation.SC_CANCEL_ROW_RFQ_CONFIRMATION,
        buttons: [Translation.TC_BUTTON_YES, ...(canCancelAndStage ? [Translation.SC_BUTTON_YES_AND_STAGE] : []), Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }

      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        onCancelBulk(negotiationIds, false)
      }

      if (dialogResult === Translation.SC_BUTTON_YES_AND_STAGE) {
        onCancelBulk(negotiationIds, true)
      }
    }

    private handleCancelPackage = async () => {
      const {
        props: { packageNegotiationSummaries, getNegotiationCountWithCollectionId, showDialog, onCancelBulk }
      } = this

      if (isNil(packageNegotiationSummaries)) {
        return
      }

      const canCancelAndStage = uniq(
        packageNegotiationSummaries.map(negotiationSummary => negotiationSummary.negotiation.collectionId).filter(notNilFilter)
      ).every(collectionId => {
        const totalNumberOfNegotiationsWithCollectionId = getNegotiationCountWithCollectionId(collectionId)

        return (
          packageNegotiationSummaries.filter(({ negotiation }) => negotiation.collectionId === collectionId).length ===
          totalNumberOfNegotiationsWithCollectionId
        )
      })

      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.SC_CANCEL_RFQS,
        message: Translation.TC_CANCEL_RFQS,
        buttons: [Translation.TC_BUTTON_YES, ...(canCancelAndStage ? [Translation.SC_BUTTON_YES_AND_STAGE] : []), Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }

      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        onCancelBulk(
          packageNegotiationSummaries.map(({ negotiation }) => negotiation.negotiationId),
          false
        )
      }

      if (dialogResult === Translation.SC_BUTTON_YES_AND_STAGE) {
        onCancelBulk(
          packageNegotiationSummaries.map(({ negotiation }) => negotiation.negotiationId),
          true
        )
      }
    }

    refresh = () => {
      this.forceUpdate()
      return true
    }
  }

  const mapStateToProps = (state: GlobalState, props: CounterpartyNegotiationOwnProps) => {
    const firstChild = props.node.group ? first(props.node.allLeafChildren) : undefined
    const data = firstChild?.data
    return {
      packageNegotiationSummaries: createInitiatedPackageNegotiationsSummarySelector(data?.negotiation.collectionId ?? undefined)(state),
      selectedNegotiationDetail:
        isNotEmpty(props.data) && isNotNil(props.data) ? createSelectedNegotiationDetailWithMeta(props.data)(state) : undefined,
      selectedNegotiationDetailSummary: data ? createSelectedNegotiationDetailSummaryWithMeta(data)(state) : undefined,
      getNegotiationCountWithRfqId: initiatedNegotiationsCountByRfqIdGetterSelector(state),
      getNegotiationCountWithCollectionId: initiatedSummaryNegotiationsCountByCollectionIdGetterSelector(state)
    }
  }

  const mapDispatchToProps = {
    onCancelBulk: NegotiationActions.cancelBulk
  }

  return compose(
    withModal,
    connect(mapStateToProps, mapDispatchToProps, null, {
      forwardRef: true
    })
  )(CounterpartyNegotiation)
}
